import Card from "components/card/Card";
import Checkbox from "components/form/checkbox/Checkbox";
import FormLayout from "components/form/FormLayout";
import Input from "components/form/input";
import Page from "components/page/Page";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Select } from "components/select/index";
import { TProductVendor } from "redux/reducers/models/product";
import axios, { CancelToken } from "axios";
import { GetVendor, PostVendor } from "api/rpc/2024-04/facilityAdmin/product/vendor";
import { StatusCode } from "api/protocols";
import { showError, showSuccess } from "redux/actions/ui";
import { capitalize, errorMessage } from "helpers/Helpers";
import ReactDOM from "react-dom";
import validator from "validator";
import useModal from "hooks/modals/useModal";
import Portal from "elements/Portal";
import Sheet from "components/sheet/Sheet";
import { PostPurchaseOrder } from "api/rpc/2024-04/facilityAdmin/order/purchaseOrder";
import { useHistory } from "react-router";

interface IState {
    // Billing
    bill_to_name: string;
    bill_to_address_line: string;
    bill_to_city: string;
    bill_to_province: string;
    bill_to_postal: string;
    bill_to_country: string;

    // Shipping
    ship_to_name: string;
    ship_to_address_line: string;
    ship_to_city: string;
    ship_to_province: string;
    ship_to_postal: string;
    ship_to_country: string;

    // Vendor
    vendor_name: string;
    vendor_address_line: string;
    vendor_city: string;
    vendor_province: string;
    vendor_postal: string;
    vendor_country: string;
    vendor_email: string;
    vendor_phone: string;
    vendor_contact_name: string;
    po_number: string;
}

export default function PurchaseOrderNew() {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const history = useHistory();
    const { facilityStore } = useAppSelector(store => store);
    const [billingSame, setBillingSame] = useState<boolean>(true);
    const [vendorSearch, setVendorSearch] = useState<string>("");
    const [vendors, setVendors] = useState<Array<TProductVendor>>([]);
    const [selectedVendor, setSelectedVendor] = useState<TProductVendor>(null);

    const { state: newVendorModal, updateModal: updateNewVendorModal, closeModal: closeNewVendorModal } = useModal<{ title: string; address_line: string; city: string; province: string; country: string; postal_code: string; email: string; phone: string; contact_name: "" }>({
        title: "",
        address_line: "",
        city: "",
        province: "",
        country: "",
        postal_code: "",
        email: "",
        phone: "",
        contact_name: "",
    });

    const { Option } = Select;
    const [state, setState] = useState<IState>({
        // Billing
        bill_to_name: "",
        bill_to_address_line: "",
        bill_to_city: "",
        bill_to_province: "",
        bill_to_postal: "",
        bill_to_country: "",

        // Shipping
        ship_to_name: "",
        ship_to_address_line: "",
        ship_to_city: "",
        ship_to_province: "",
        ship_to_postal: "",
        ship_to_country: "",

        // Vendor
        vendor_name: "",
        vendor_address_line: "",
        vendor_city: "",
        vendor_province: "",
        vendor_postal: "",
        vendor_country: "",
        vendor_email: "",
        vendor_phone: "",
        vendor_contact_name: "",
        po_number: "",
    });

    useEffect(() => {
        if (facilityStore?.facility) {
            setState(prev => ({
                ...prev,
                bill_to_name: facilityStore?.facility?.long_name,
                bill_to_address_line: facilityStore?.facility?.address_line_1,
                bill_to_city: facilityStore?.facility?.city,
                bill_to_province: facilityStore?.facility?.province_name,
                bill_to_postal: facilityStore?.facility?.postal,
                bill_to_country: facilityStore?.facility?.country_name,

                ship_to_name: facilityStore?.facility?.long_name,
                ship_to_address_line: facilityStore?.facility?.address_line_1,
                ship_to_city: facilityStore?.facility?.city,
                ship_to_province: facilityStore?.facility?.province_name,
                ship_to_postal: facilityStore?.facility?.postal,
                ship_to_country: facilityStore?.facility?.country_name,
            }));
        }
    }, [facilityStore?.facility]);

    useEffect(() => {
        const source = axios.CancelToken.source();

        let timeoutId: NodeJS.Timeout = null;
        timeoutId = global.setTimeout(() => {
            void searchVendors(source.token, vendorSearch);
        }, 500);

        return () => {
            clearTimeout(timeoutId);
            setVendors([]);
            source.cancel();
        };
    }, [vendorSearch]);

    async function searchVendors(cancelToken: CancelToken, searchQuery: string) {
        if (vendors) {
            setVendors(undefined);
        }
        const vendorRes = await GetVendor({ search: searchQuery }, false, cancelToken);
        if (vendorRes?.status !== StatusCode.OK) {
            setVendors([]);
            if (cancelToken && cancelToken?.reason) {
                return;
            }
            dispatch(showError(errorMessage(vendorRes, t("Error getting vendors"))));
            return;
        }
        setVendors(vendorRes?.data);
    }

    function onInputChange(e: React.ChangeEvent<HTMLInputElement>) {
        const { id, value } = e.target;
        setState(prev => ({ ...prev, [id]: value }));
    }

    function sameShippingCheck(e: React.ChangeEvent<HTMLInputElement>) {
        const { checked } = e.target;
        if (checked) {
            setState(prev => ({
                ...prev,
                bill_to_name: prev?.ship_to_name,
                bill_to_address_line: prev?.ship_to_address_line,
                bill_to_city: prev?.ship_to_city,
                bill_to_province: prev?.ship_to_province,
                bill_to_postal: prev?.ship_to_postal,
                bill_to_country: prev?.ship_to_country,
            }));
        }
        setBillingSame(checked);
    }

    function vendorModalInputChange(e: React.ChangeEvent<HTMLInputElement>) {
        const { id, value } = e.target;
        updateNewVendorModal({ [id]: value });
    }

    async function saveNewVendor() {
        const params = {
            ...newVendorModal,
        };

        if (newVendorModal?.email && !validator?.isEmail(newVendorModal?.email)) {
            dispatch(showError(t("The email address format is invalid")));
            return;
        }

        const res = await PostVendor(params, true);
        if (res.status !== StatusCode.OK) {
            dispatch(showError(errorMessage(res, t("Error creating vendor"))));
            return;
        }

        dispatch(showSuccess(t("Successfully created vendor")));
        const vendor = res?.data;
        ReactDOM.unstable_batchedUpdates(() => {
            setVendorSearch(vendor?.title);
            setState(prev => ({
                ...prev,
                vendor_name: vendor?.title ?? "",
                vendor_address_line: vendor?.address_line ?? "",
                vendor_city: vendor?.city ?? "",
                vendor_province: vendor?.province ?? "",
                vendor_postal: vendor?.postal_code ?? "",
                vendor_country: vendor?.country ?? "",
                vendor_email: vendor?.email ?? "",
                vendor_phone: vendor?.phone ?? "",
                vendor_contact_name: vendor?.contact_name ?? "",
            }));
        });
        void closeNewVendorModal();
    }

    async function createPurchaseOrder() {

        if (state?.vendor_email && !validator.isEmail(state?.vendor_email)) {
            dispatch(showError(t("The email address format is invalid")));
            return;
        }
        
        const res = await PostPurchaseOrder({
            vendor_id: selectedVendor?.id ?? null,

            status: "draft",
            po_number: state.po_number,
        
            // Bill To
            bill_to_name: state.bill_to_name,
            bill_to_address_line: state.bill_to_address_line,
            bill_to_city: state.bill_to_city,
            bill_to_province: state.bill_to_province,
            bill_to_postal: state.bill_to_postal,
            bill_to_country: state.bill_to_country,
        
            // Ship To
            ship_to_name: state.ship_to_name,
            ship_to_address_line: state.ship_to_address_line,
            ship_to_city: state.ship_to_city,
            ship_to_province: state.ship_to_province,
            ship_to_postal: state.ship_to_postal,
            ship_to_country: state.ship_to_country,
        
            // Vendor
            vendor_name: state.vendor_name,
            vendor_address_line: state.vendor_address_line,
            vendor_city: state.vendor_city,
            vendor_province: state.vendor_province,
            vendor_postal: state.vendor_postal,
            vendor_country: state.vendor_country,
            vendor_contact_name: state.vendor_contact_name || null,
            vendor_email: state.vendor_email || null,
            vendor_phone: state.vendor_phone || null,
        
            notes: null,
        }, true);

        if (res?.status !== StatusCode.OK) {
            dispatch(showError(errorMessage(res, t("Error creating purchase order"))));
            return;
        }
        dispatch(showSuccess(t("Successfully created purchase order draft")));
        history.push(`/admin/settings/purchase-orders/${res?.data?.id}`);
    }

    return (
        <>

            <Page
                title={t("Purchase Orders")}
                narrow
                breadcrumbs={[{
                    prefix: true,
                    label: t("Back to purchase orders"),
                    url: "/admin/settings/purchase-orders",
                }]}
                primaryAction={{ content: t("Create"), action: () => createPurchaseOrder() }}
            >
                <Card title={t("Purchase Order Details")}>
                    <Card.Section>
                        <FormLayout>
                            <FormLayout.Group>
                                <Input
                                    label={t("PO Number")}
                                    value={state?.po_number}
                                    onChange={onInputChange}
                                    id="po_number"
                                    placeholder={t("PO Number")}
                                />
                            </FormLayout.Group>
                        </FormLayout>
                    </Card.Section>
                    <Card.Section>
                        <FormLayout>
                            <FormLayout.Group>
                                <Select
                                    label={t("Vendor")}
                                    placeholder={t("Vendor")}
                                    showSearch
                                    onSearch={(query: string) => {
                                        setVendorSearch(query);
                                        if (!query) {
                                            setSelectedVendor(null);
                                        }
                                    }}
                                    onChange={(_, vendor: TProductVendor) => {
                                        ReactDOM.unstable_batchedUpdates(() => {
                                            setSelectedVendor(vendor);
                                            setVendorSearch(vendor?.title);
                                            setState(prev => ({
                                                ...prev,
                                                vendor_name: vendor?.title ?? "",
                                                vendor_address_line: vendor?.address_line ?? "",
                                                vendor_city: vendor?.city ?? "",
                                                vendor_province: vendor?.province ?? "",
                                                vendor_postal: vendor?.postal_code ?? "",
                                                vendor_country: vendor?.country ?? "",
                                                vendor_email: vendor?.email ?? "",
                                                vendor_phone: vendor?.phone ?? "",
                                                vendor_contact_name: vendor?.contact_name ?? "",
                                            }));
                                        });
                                    }}
                                    allowClear
                                    searchValue={vendorSearch}
                                    showDropDownOnFocus={true}
                                    searching={vendors === undefined}
                                    defaultValue={selectedVendor?.id}
                                    usePortal
                                >
                                    <div
                                        className="ui-select-dropdown-list-item"
                                        onClick={() => updateNewVendorModal({ isOpen: true, title: capitalize(vendorSearch ?? "") })}
                                    >
                                        <p className="text-md">{t("New")}</p>
                                    </div>
                                    {vendors?.map((vendor, index) => {
                                        return (
                                            <Option key={index} value={vendor.id} extraValues={vendor}>
                                                {vendor?.title}
                                            </Option>
                                        );
                                    })}
                                </Select>
                            </FormLayout.Group>
                        </FormLayout>
                    </Card.Section>
                    <Card.Section>
                        <FormLayout>
                            <FormLayout.Group>
                                <Input
                                    label={t("Vendor Name")}
                                    value={state?.vendor_name}
                                    onChange={onInputChange}
                                    id="vendor_name"
                                    placeholder={t("Vendor Name")}
                                />
                            </FormLayout.Group>
                            <FormLayout.Group>
                                <Input
                                    label={t("Address Line")}
                                    value={state?.vendor_address_line}
                                    onChange={onInputChange}
                                    id="vendor_address_line"
                                    placeholder={t("Address Line")}
                                />
                            </FormLayout.Group>
                            <FormLayout.Group>
                                <Input
                                    label={t("City")}
                                    value={state?.vendor_city}
                                    onChange={onInputChange}
                                    id="vendor_city"
                                    placeholder={t("City")}
                                />
                                <Input
                                    label={t("Province")}
                                    value={state?.vendor_province}
                                    onChange={onInputChange}
                                    id="vendor_province"
                                    placeholder={t("Province")}
                                />
                            </FormLayout.Group>
                            <FormLayout.Group>
                                <Input
                                    label={t("Country")}
                                    value={state?.vendor_country}
                                    onChange={onInputChange}
                                    id="vendor_country"
                                    placeholder={t("Country")}
                                />
                                <Input
                                    label={t("Postal Code")}
                                    value={state?.vendor_postal}
                                    onChange={onInputChange}
                                    id="vendor_postal"
                                    placeholder={t("Postal Code")}
                                />
                            </FormLayout.Group>
                        </FormLayout>
                    </Card.Section>
                    <Card.Section>
                        <FormLayout>
                            <FormLayout.Group>
                                <Input
                                    label={t("Email")}
                                    value={state?.vendor_email}
                                    onChange={onInputChange}
                                    id="vendor_email"
                                    placeholder={t("Email")}
                                    type="email"
                                    error={state?.vendor_email && !validator.isEmail(state?.vendor_email)}
                                />
                                <Input
                                    label={t("Phone")}
                                    value={state?.vendor_phone}
                                    onChange={onInputChange}
                                    id="vendor_phone"
                                    placeholder={t("Phone")}
                                    type="number"
                                />
                            </FormLayout.Group>
                            <FormLayout.Group>
                                <Input
                                    label={t("Contact Name")}
                                    value={state?.vendor_contact_name}
                                    onChange={onInputChange}
                                    id="vendor_contact_name"
                                    placeholder={t("Contact Name")}
                                />
                            </FormLayout.Group>
                        </FormLayout>
                    </Card.Section>
                </Card>
                <Card title={t("Ship to")}>
                    <Card.Section>
                        <FormLayout>
                            <FormLayout.Group>
                                <Input
                                    label={t("Facility Name")}
                                    value={state?.ship_to_name}
                                    onChange={onInputChange}
                                    id="ship_to_name"
                                    placeholder={t("Facility Name")}
                                />
                            </FormLayout.Group>
                        </FormLayout>
                    </Card.Section>
                    <Card.Section>
                        <FormLayout>
                            <FormLayout.Group>
                                <Input
                                    label={t("Address Line")}
                                    value={state?.ship_to_address_line}
                                    onChange={onInputChange}
                                    id="ship_to_address_line"
                                    placeholder={t("Address Line")}
                                />
                            </FormLayout.Group>
                            <FormLayout.Group>
                                <Input
                                    label={t("City")}
                                    value={state?.ship_to_city}
                                    onChange={onInputChange}
                                    id="ship_to_city"
                                    placeholder={t("City")}
                                />
                                <Input
                                    label={t("Province")}
                                    value={state?.ship_to_province}
                                    onChange={onInputChange}
                                    id="ship_to_province"
                                    placeholder={t("Province")}
                                />
                            </FormLayout.Group>
                            <FormLayout.Group>
                                <Input
                                    label={t("Country")}
                                    value={state?.ship_to_country}
                                    onChange={onInputChange}
                                    id="ship_to_country"
                                    placeholder={t("Country")}
                                />
                                <Input
                                    label={t("Postal Code")}
                                    value={state?.ship_to_postal}
                                    onChange={onInputChange}
                                    id="ship_to_postal"
                                    placeholder={t("Postal Code")}
                                />
                            </FormLayout.Group>
                        </FormLayout>
                    </Card.Section>
                </Card>

                <Card title={t("Bill to")} titleDetails={[{ content: <Checkbox checked={billingSame} onChange={sameShippingCheck} label={t("Same as shipping address")} size="medium" /> }]}>
                    {!billingSame ? (
                        <>
                            <Card.Section>
                                <FormLayout>
                                    <FormLayout.Group>
                                        <Input
                                            label={t("Facility Name")}
                                            value={state?.bill_to_name}
                                            onChange={onInputChange}
                                            id="bill_to_name"
                                            placeholder={t("Facility Name")}
                                        />
                                    </FormLayout.Group>
                                </FormLayout>
                            </Card.Section>
                            <Card.Section>
                                <FormLayout>
                                    <FormLayout.Group>
                                        <Input
                                            label={t("Address Line")}
                                            value={state?.bill_to_address_line}
                                            onChange={onInputChange}
                                            id="bill_to_address_line"
                                            placeholder={t("Address Line")}
                                        />
                                    </FormLayout.Group>
                                    <FormLayout.Group>
                                        <Input
                                            label={t("City")}
                                            value={state?.bill_to_city}
                                            onChange={onInputChange}
                                            id="bill_to_city"
                                            placeholder={t("City")}
                                        />
                                        <Input
                                            label={t("Province")}
                                            value={state?.bill_to_province}
                                            onChange={onInputChange}
                                            id="bill_to_province"
                                            placeholder={t("Province")}
                                        />
                                    </FormLayout.Group>
                                    <FormLayout.Group>
                                        <Input
                                            label={t("Country")}
                                            value={state?.bill_to_country}
                                            onChange={onInputChange}
                                            id="bill_to_country"
                                            placeholder={t("Country")}
                                        />
                                        <Input
                                            label={t("Postal Code")}
                                            value={state?.bill_to_postal}
                                            onChange={onInputChange}
                                            id="bill_to_postal"
                                            placeholder={t("Postal Code")}
                                        />
                                    </FormLayout.Group>
                                </FormLayout>
                            </Card.Section>
                        </>
                    ) : <div className="p-2"></div>}
                </Card>
            </Page>
            <Portal isMounted={newVendorModal?.isOpen}>
                <Sheet
                    title={t("New vendor")}
                    open={newVendorModal?.isOpen}
                    size="small"
                    stacked
                    onCancel={closeNewVendorModal}
                    onOk={saveNewVendor}
                    okText={t("Create")}
                    closable
                >
                    <FormLayout>
                        <FormLayout.Group>
                            <Input
                                value={newVendorModal.title}
                                label={t("Name")}
                                id="title"
                                onChange={vendorModalInputChange}
                                placeholder={t("Name")}
                            />
                        </FormLayout.Group>
                        <FormLayout.Group>
                            <Input
                                value={newVendorModal.address_line}
                                label={t("Address")}
                                id="address_line"
                                onChange={vendorModalInputChange}
                                placeholder={t("Address")}
                            />
                        </FormLayout.Group>
                        <FormLayout.Group>
                            <Input
                                value={newVendorModal.city}
                                label={t("City")}
                                id="city"
                                onChange={vendorModalInputChange}
                                placeholder={t("City")}
                            />
                            <Input
                                value={newVendorModal.province}
                                label={t("Province")}
                                id="province"
                                onChange={vendorModalInputChange}
                                placeholder={t("Province")}
                            />
                        </FormLayout.Group>
                        <FormLayout.Group>
                            <Input
                                value={newVendorModal.country}
                                label={t("Country")}
                                id="country"
                                onChange={vendorModalInputChange}
                                placeholder={t("Country")}
                            />
                            <Input
                                value={newVendorModal.postal_code}
                                label={t("Postal Code")}
                                id="postal_code"
                                onChange={vendorModalInputChange}
                                placeholder={t("Postal Code")}
                            />
                        </FormLayout.Group>
                        <FormLayout.Group>
                            <Input
                                value={newVendorModal.email}
                                label={t("Email")}
                                id="email"
                                type="email"
                                onChange={vendorModalInputChange}
                                placeholder={t("Email")}
                                error={newVendorModal?.email && !validator.isEmail(newVendorModal?.email)}
                            />
                            <Input
                                value={newVendorModal.phone}
                                label={t("Phone")}
                                id="phone"
                                type="number"
                                onChange={vendorModalInputChange}
                                placeholder={t("Phone")}
                                error={newVendorModal?.phone && !validator?.isNumeric(newVendorModal?.phone)}
                            />
                        </FormLayout.Group>
                        <FormLayout.Group>
                            <Input
                                value={newVendorModal.contact_name}
                                label={t("Contact Name")}
                                id="contact_name"
                                onChange={vendorModalInputChange}
                                placeholder={t("Contact Name")}
                            />
                        </FormLayout.Group>
                    </FormLayout>
                </Sheet>
            </Portal>
        </>
    );
}